import React, {useState, useEffect} from 'react';
import ReactDOM from 'react-dom';
import Images from '../../models/image/Images';
import {getLocalItem, setLocalItem, unsetLocalItem} from '../../utils/local-storage';
import Products from '../../models/product/Products';
import __ from '../../i18n';
import {getLayoutPortalElement} from '../../dom/element/query';
import Popup from '../../components/popup/Popup';
import CartCheckoutTool from '../tools/cart-checkout/CartCheckoutTool';

export default function CartHandler(container) {

    const VERSION = 2;

    ensureVersionUpdate(VERSION);

    const listeners = [];

    const trigger = () => {
        listeners.forEach(fn => fn());
    };

    this.addListener = (fn) => {
        if (!listeners.includes(fn)) {
            listeners.push(fn);
        }
    };

    this.removeListener = (fn) => {
        if (listeners.includes(fn)) {
            listeners.splice(listeners.indexOf(fn), 1);
        }
    };

    this.addToCart = (product, image, option = null, promotion = null) => {
        addCartItem({
            id: product.id + ';' + (option ? option.id : ''),
            name: __(':product:option(SELECT, **, [ ]- :@)', {
                product: product.name,
                option: option ? option.name : '',
            }),
            url: Products.getUrl(product, option),
            quantity: 1,
            unit_price: Products.getSalePrice(product, promotion),
            is_price_public: product.is_price_public,
            image_src: Images.getUrl(image),

            product_id: product.id,
            product_name: product.name,
            product_code: product.code,
            product_price: product.price,
            product_condition_level: product.condition_level,

            product_option_id: option && option.id,
            product_option_name: option && option.name,

            promotion_id: promotion && promotion.id,
            promotion_name: promotion && promotion.name
        });

        trigger();
    };

    this.addProductExtraToCart = (productExtra, image) => {
        addCartItem({
            id: productExtra.product_id + ';' + productExtra.product_option_id,
            name: productExtra.name, // product.name + option.name by SQL views
            url: Products.createUrl(productExtra.slug, productExtra.product_option_id),
            quantity: 1,
            unit_price: productExtra.sale_price,
            is_price_public: productExtra.is_price_public,
            image_src: Images.getUrl(image),

            product_id: productExtra.product_id,
            product_name: productExtra.product_name,
            product_code: productExtra.product_code,
            product_price: productExtra.price,
            product_condition_level: productExtra.condition_level,

            product_option_id: productExtra.product_option_id,
            product_option_name: productExtra.product_option_name,

            promotion_id: productExtra.prommotion_id,
            promotion_name: productExtra.promotion_name
        });

        trigger();
    };

    this.removeFromCart = (itemId) => {
        removeCartItem(itemId);
        trigger();
    };

    this.updateCartItem = (itemId, values) => {
        updateCartItem(itemId, values);
        trigger();
    };

    this.clearCart = () => {
        clearCart();
        trigger();
    };

    this.countItems = () => {
        return getCartItems().length;
    };

    this.popupContainer = document.createElement('div');

    getLayoutPortalElement().appendChild(this.popupContainer);

    this.HIDE_TIMEOUT = 2000;

    this.getPopup = () => {
        let listener = null;

        return <Popup>
            <CartCheckoutTool
                cartItems={getCartItems()}
                updateItem={this.updateCartItem}
                removeItem={(itemId) => {
                    this.removeFromCart(itemId);

                    if (this.countItems() === 0) {
                        setTimeout(() => {
                            this.hidePopup();
                        }, this.HIDE_TIMEOUT);
                    }
                }}
                clearCart={this.clearCart}
                cancel={this.hidePopup}
                isPreviewMode={false}
                onExposeSetCartItems={fn => {
                    listener = () => fn(getCartItems());
                    this.addListener(listener);
                }}
                releaseSetCartItems={fn => {
                    this.removeListener(listener);
                }}
            />
        </Popup>;
    };

    this.showPopup = () => {
        ReactDOM.render(this.getPopup(), this.popupContainer);
    };

    this.hidePopup = () => {
        ReactDOM.unmountComponentAtNode(this.popupContainer);
    };

    this.renderCart = () => {
        ReactDOM.render(<CartCheckoutTool
            cartItems={getCartItems()}
            updateItem={this.updateCartItem}
            removeItem={this.removeFromCart}
            isPreviewMode={true}
            openFullMode={this.showPopup}
        />, container);
    };

    this.unmountCart = () => {
        ReactDOM.unmountComponentAtNode(container);
    }
}


const KEY_CART_ITEMS = 'cart_items';
const KEY_CART_VERSION = 'cart_version';

function ensureVersionUpdate(version) {
    const localVersion = getLocalItem(KEY_CART_VERSION, 0);

    if (localVersion < version) {
        clearCart();
        setLocalItem(KEY_CART_VERSION, version);
    }
}

function getCartItems() {
    return getLocalItem(KEY_CART_ITEMS, []);
}

function addCartItem(item) {
    const cartItems = getCartItems();

    if (cartItems.some(t => t.id === item.id)) {
        return;
    }

    cartItems.push(item);

    setLocalItem(KEY_CART_ITEMS, cartItems);
}

function clearCart() {
    unsetLocalItem(KEY_CART_ITEMS);
}

function removeCartItem(itemId) {
    const cartItems = getCartItems();

    const removedItem = cartItems.find(t => t.id === itemId);
    if (!removedItem) {
        return;
    }

    cartItems.splice(cartItems.indexOf(removedItem), 1);

    setLocalItem(KEY_CART_ITEMS, cartItems);
}

function updateCartItem(itemId, values) {
    const cartItems = getCartItems();

    const updatedItem = cartItems.find(t => t.id === itemId);
    if (!updatedItem) {
        return;
    }

    for (let field in updatedItem) {
        if (updatedItem.hasOwnProperty(field) && values.hasOwnProperty(field)) {
            updatedItem[field] = values[field];
        }
    }

    setLocalItem(KEY_CART_ITEMS, cartItems);
}
